import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FiEdit, FiPlus, FiTrash2 } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createRouteMap,
  deleteRouteMap,
  fetchAvailableRouteMapThings,
  fetchRouteMaps,
  updateRouteMap,
} from "../../../services/adminRestapiService";
import TopBar from "../../common/TopBar";
import Sidebar from "../../common/Sidebar";
import { getImageUploadError, IMAGE_UPLOAD_ACCEPT, IMAGE_UPLOAD_HELP } from "../../../utils/imageUpload";

const API_URL = "https://api.gosehari.com";
const emptyRouteMap = () => ({ thing_id: "", image_path: null });
const getImageUrl = (imagePath) => imagePath?.startsWith("http") || imagePath?.startsWith("blob:") ? imagePath : `${API_URL}${imagePath}`;

function RouteMaps() {
  const [routeMaps, setRouteMaps] = useState([]);
  const [availableThings, setAvailableThings] = useState([]);
  const [canAdd, setCanAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRouteMap, setEditingRouteMap] = useState(null);
  const [form, setForm] = useState(emptyRouteMap);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");

  const loadRouteMaps = async () => {
    setLoading(true);
    try {
      setRouteMaps((await fetchRouteMaps(token)).data.data || []);
    } catch {
      toast.error("Failed to load route maps");
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableThings = async () => {
    try {
      setAvailableThings((await fetchAvailableRouteMapThings(token)).data.data || []);
    } catch {
      setAvailableThings([]);
      toast.error("Failed to load available Top Things");
    }
  };

  const refreshAddAvailability = async () => {
    try {
      const things = (await fetchAvailableRouteMapThings(token)).data.data || [];
      setCanAdd(things.some((thing) => !thing.is_mapped));
    } catch {
      setCanAdd(false);
    }
  };

  useEffect(() => {
    loadRouteMaps();
    refreshAddAvailability();
  }, []);

  const openAdd = async () => {
    await loadAvailableThings();
    setEditingRouteMap(null);
    setForm(emptyRouteMap());
    setImageFile(null);
    setDialogOpen(true);
  };

  const openEdit = async (routeMap) => {
    await loadAvailableThings();
    setEditingRouteMap(routeMap);
    setForm({ thing_id: String(routeMap.thing_id), image_path: routeMap.image_path });
    setImageFile(null);
    setDialogOpen(true);
  };

  const selectImage = (file, input) => {
    const imageError = getImageUploadError(file);
    if (imageError) {
      toast.error(imageError);
      input.value = "";
      return;
    }
    setImageFile(file || null);
  };

  const submit = async () => {
    if (!form.thing_id) return toast.error("Select a Top Thing");
    if (!imageFile) return toast.error("Upload one route map image");

    setSaving(true);
    try {
      const payload = new FormData();
      payload.append("thing_id", form.thing_id);
      payload.append("image", imageFile);
      if (editingRouteMap) await updateRouteMap(token, editingRouteMap.thing_id, payload);
      else await createRouteMap(token, payload);

      toast.success(editingRouteMap ? "Route map updated" : "Route map created");
      setDialogOpen(false);
      await Promise.all([loadRouteMaps(), refreshAddAvailability()]);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save route map");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (routeMap) => {
    if (!window.confirm(`Delete the route map for “${routeMap.thing_title}”?`)) return;
    try {
      await deleteRouteMap(token, routeMap.thing_id);
      toast.success("Route map deleted; its Top Thing is available again");
      await Promise.all([loadRouteMaps(), refreshAddAvailability()]);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to delete route map");
    }
  };

  const columns = [
    { field: "thing_id", headerName: "Top Thing ID", width: 105 },
    { field: "thing_title", headerName: "Top Thing", flex: 1.3, minWidth: 200 },
    { field: "thing_category", headerName: "Category", width: 140 },
    {
      field: "image_path",
      headerName: "Route map image",
      width: 180,
      sortable: false,
      renderCell: ({ value, row }) => value ? <img src={getImageUrl(value)} alt={`${row.thing_title} route map`} className="h-14 w-36 rounded-lg border border-slate-200 object-cover" /> : null,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 125,
      sortable: false,
      renderCell: ({ row }) => <Box><IconButton aria-label="Edit route map" color="primary" onClick={() => openEdit(row)}><FiEdit /></IconButton><IconButton aria-label="Delete route map" color="error" onClick={() => remove(row)}><FiTrash2 /></IconButton></Box>,
    },
  ];

  const hasAvailableThings = availableThings.some((thing) => !thing.is_mapped);
  const selectedTopThing = availableThings.find((thing) => String(thing.id) === String(form.thing_id));
  const preview = imageFile ? URL.createObjectURL(imageFile) : form.image_path;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={`min-h-screen min-w-0 flex-1 transition-[margin] duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"}`}>
        <TopBar toggleSidebar={() => setSidebarOpen((open) => !open)} sidebarOpen={sidebarOpen} />
        <main className="space-y-6 px-4 pb-8 pt-24 sm:px-6 lg:px-8">
          <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Route Maps</h1>
                <p className="mt-1 text-sm text-slate-600">Manage one route map image for each Top Thing.</p>
                {!loading && !canAdd && <p className="mt-1 text-sm text-amber-700">All Top Things already have a route map.</p>}
              </div>
              <Button variant="contained" startIcon={<FiPlus />} onClick={openAdd} disabled={loading || !canAdd} className="self-start sm:self-auto">Add Route Map</Button>
            </div>
            <div className="min-w-0 overflow-x-auto rounded-xl border border-slate-100"><DataGrid rows={routeMaps} columns={columns} loading={loading} autoHeight rowHeight={68} disableRowSelectionOnClick pageSizeOptions={[10, 25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} sx={{ minWidth: 760, border: 0 }} /></div>
          </section>
        </main>
      </div>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingRouteMap ? "Edit Route Map" : "Add Route Map"}</DialogTitle>
        <DialogContent>
          {!editingRouteMap && !hasAvailableThings ? <p className="py-4 text-slate-700">All Top Things already have a route map.</p> : (
            <Box component="form" className="flex flex-col gap-4 pt-3">
              {editingRouteMap ? <div><span className="mb-1 block text-sm">Top Thing</span><div className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700">{editingRouteMap.thing_title}</div></div> : (
                <label><span className="mb-1 block text-sm">Top Thing *</span><select value={form.thing_id} onChange={(event) => setForm((current) => ({ ...current, thing_id: event.target.value }))} className="w-full rounded-lg border border-slate-300 p-3" required><option value="">Select a Top Thing</option>{availableThings.map((thing) => <option key={thing.id} value={thing.id} disabled={thing.is_mapped}>{thing.title} ({thing.category}){thing.is_mapped ? " — Route map already added" : ""}</option>)}</select></label>
              )}
              {selectedTopThing && !editingRouteMap && <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><p className="font-semibold text-slate-800">{selectedTopThing.title}</p><p className="mt-1 text-sm text-slate-500">{selectedTopThing.category}</p><p className="mt-1 text-sm text-slate-600">{selectedTopThing.description || "No description available."}</p></div>}
              <div><p className="mb-2 text-sm">Route map image *</p>{preview ? <img src={getImageUrl(preview)} alt="Route map preview" className="h-56 w-full rounded-lg border border-slate-200 object-contain" /> : <div className="flex h-56 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-400">No route map image selected</div>}<input className="mt-3 block w-full" type="file" accept={IMAGE_UPLOAD_ACCEPT} onChange={(event) => selectImage(event.target.files[0], event.target)} /><p className="mt-1 text-xs text-slate-500">{editingRouteMap ? "Choose a replacement image to update this route map. " : ""}{IMAGE_UPLOAD_HELP}</p></div>
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>{(editingRouteMap || hasAvailableThings) && <Button variant="contained" onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>}</DialogActions>
      </Dialog>
    </div>
  );
}

export default RouteMaps;
